import {
  isTextSelectionEntity,
  textSelectionPillLabel,
  textSelectionSelectedText,
} from "@/panes/file-record-detail/fileRecordTextSelection";
import { cn } from "@/utils";
import type { ReferencedEntity } from "@cohesive/api-client";
import { FileRecordTypeIcon, IntegrationIcon } from "@cohesive/ui-components";
import { Quote, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";

const DEFAULT_MAX_VISIBLE = 2;

/** File-backed referenced entities (workspace/canvas file records). */
function isFileEntity(entity: ReferencedEntity): boolean {
  return entity.entity_type === "file" || entity.entity_type === "file_record";
}

/**
 * Guess a MIME type from a filename extension so file-record pills can show
 * the same coloured type icon the explorer uses. The pill only carries the
 * entity name (not content_type), so this maps the extension to the closest
 * MIME string `FileRecordTypeIcon` understands.
 */
function mimeFromFilename(name: string): string {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  switch (ext) {
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "webp":
    case "svg":
    case "bmp":
    case "heic":
      return "image/png";
    case "pdf":
      return "application/pdf";
    case "csv":
      return "text/csv";
    case "tsv":
      return "text/tab-separated-values";
    case "rtf":
      return "application/rtf";
    case "xls":
    case "xlsx":
      return "application/vnd.ms-excel";
    case "mp4":
    case "mov":
    case "webm":
    case "avi":
    case "mkv":
      return "video/mp4";
    case "mp3":
    case "wav":
    case "m4a":
    case "ogg":
    case "flac":
      return "audio/mpeg";
    case "doc":
    case "docx":
      return "application/msword";
    case "json":
      return "application/json";
    case "js":
    case "jsx":
      return "text/javascript";
    case "ts":
    case "tsx":
      return "text/typescript";
    case "go":
      return "text/x-go";
    case "sh":
    case "bash":
      return "application/x-sh";
    case "py":
      return "text/x-python";
    case "rs":
      return "text/x-rust";
    case "html":
      return "text/html";
    case "css":
      return "text/css";
    case "xml":
      return "application/xml";
    case "md":
    case "markdown":
    case "txt":
      return "text/plain";
    default:
      return "";
  }
}

interface ReferencedEntitiesPillsProps {
  entities: ReferencedEntity[];
  onRemove?: (entityId: string) => void;
  className?: string;
  /** How many pills to show before the "+N" overflow badge. Defaults to 2. */
  maxVisible?: number;
}

export function ReferencedEntitiesPills({
  entities,
  onRemove,
  className,
  maxVisible = DEFAULT_MAX_VISIBLE,
}: ReferencedEntitiesPillsProps) {
  if (entities.length === 0) return null;

  const visible = entities.slice(0, maxVisible);
  const overflow = entities.length - maxVisible;

  return (
    <div
      className={cn(
        "flex items-center gap-1.5 px-3 py-2 overflow-hidden",
        className,
      )}
    >
      <AnimatePresence mode="popLayout">
        {visible.map((entity) => {
          const isTextSelection = isTextSelectionEntity(entity);

          return (
            <motion.div
              key={entity.entity_id}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.1 }}
              className={cn(
                "flex items-center gap-1 pl-1 py-0.5 rounded-full min-w-0 shrink",
                "text-[11px]",
                isTextSelection
                  ? "bg-spectrum-violet/10 border border-violet-500/30 text-violet-700 dark:text-violet-300"
                  : "bg-transparent border text-foreground",
                onRemove ? "pr-0.5" : "pr-2",
              )}
              title={
                isTextSelection ? textSelectionSelectedText(entity) : undefined
              }
            >
              {isTextSelection ? (
                <Quote className="h-3.5 w-3.5 shrink-0 ml-0.5 rotate-z-180" />
              ) : isFileEntity(entity) ? (
                <FileRecordTypeIcon
                  contentType={mimeFromFilename(entity.name)}
                  className="h-4 w-4 shrink-0 ml-0.5"
                />
              ) : (
                <IntegrationIcon
                  platform={entity.platform}
                  className="h-4 w-4 rounded-full shrink-0"
                />
              )}
              <span className="max-w-[80px] truncate leading-none py-1">
                {isTextSelection ? textSelectionPillLabel(entity) : entity.name}
              </span>
              {onRemove && (
                <button
                  type="button"
                  onClick={() => onRemove(entity.entity_id)}
                  className="h-5 w-5 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors shrink-0"
                  aria-label={`Remove ${entity.name}`}
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              )}
            </motion.div>
          );
        })}

        {overflow > 0 && (
          <motion.div
            key="overflow"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.1 }}
            className={cn(
              "flex items-center justify-center px-2",
              "bg-transparent border rounded-full",
              "text-[11px] leading-none text-muted-foreground shrink-0",
              "h-6",
            )}
          >
            +{overflow}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
